import { Button, Drawer, Input, Prompt, Select, Switch, Text, toast } from "@medusajs/ui"
import { useState } from "react"
import { Field } from "@/components/form"
import { Section } from "@/components/ui"
import { formatDate } from "@/lib/format"
import { errorMessage } from "@/lib/sdk"
import { useConnectKlaviyo, useCreateStoreEmailTemplate, useDisconnectKlaviyo, useKlaviyo, useKlaviyoLists, useKlaviyoOptions, useStoreEmailSetup, useSyncKlaviyo, useTestKlaviyo } from "@/lib/klaviyo"
import { Badge, Banner, DefinitionList } from "@/terabizz"

/** The scopes a custom Klaviyo private key needs (shown in the connect drawer and docs/klaviyo.md). */
const SCOPES: [string, string][] = [
  ["Accounts", "Read"],
  ["Events", "Full"],
  ["List", "Full"],
  ["Metrics", "Read"],
  ["Profiles", "Full"],
  ["Subscriptions", "Full"],
  // Store emails through Klaviyo: the "Terify store email" template.
  ["Templates", "Full"],
  ["Flows", "Read"],
]

const NONE = "__none__"

/**
 * Settings → Customer events → Klaviyo. Connect with a private API key; orders, checkouts,
 * customers and email consent go to Klaviyo, and klaviyo.js runs on the storefront (behind the
 * cookie banner). The key is stored encrypted and never shown again (last 4 only). Only the
 * owner and staff with full permissions may change it; everyone with settings access sees the status.
 */
/** Every store email (orders, shipping, account, staff) through this Klaviyo account, by one flow. */
function StoreEmails({ on, busy, onChange }: { on: boolean; busy: boolean; onChange: (v: boolean) => void }) {
  const setup = useStoreEmailSetup(true)
  const create = useCreateStoreEmailTemplate()
  const d = setup.data
  const ready = !!d?.template_id && !!d?.metric
  return (
    <div className="flex flex-col gap-2 rounded-md border border-ui-border-base p-3 text-[13px]">
      <label className="flex items-start gap-2">
        <Switch checked={on} disabled={busy} onCheckedChange={onChange} aria-label="Send the store's emails through Klaviyo" />
        <span>
          Send the store's emails through Klaviyo
          <Text size="xsmall" className="text-ui-fg-subtle">
            Order confirmations, shipping and delivery updates, refunds, account and sign-in emails, subscription emails and staff emails. Keel still writes each email and decides when it goes; Klaviyo delivers it from your Klaviyo sending domain. Recovery's card emails follow Recovery → Settings.
          </Text>
        </span>
      </label>
      <ol className="list-decimal pl-5 text-[12px] text-ui-fg-subtle">
        <li>
          {d?.template_id ? <span className="text-ui-fg-base">Template "{d.template_name}" is in Klaviyo. </span> : null}
          <Button size="small" variant="secondary" className="ml-1" isLoading={create.isPending} onClick={() => create.mutate(undefined, { onSuccess: (r) => toast.success(r.template_id ? "Template ready in Klaviyo" : "Klaviyo didn't return the template"), onError: (e) => toast.error(errorMessage(e)) })}>
            {d?.template_id ? "Refresh the template" : "Create the template in Klaviyo"}
          </Button>
        </li>
        <li>
          Turn the switch on and send one email (Settings → Notifications → any template → Send test) so the metric "{d?.metric_name ?? "Terify Store Email"}" appears in Klaviyo. {d?.metric ? <span className="text-ui-fg-base">Received.</span> : <span>Not received yet.</span>}
        </li>
        <li>In Klaviyo: Flows → Create from scratch → trigger: that metric → add an Email using the template, subject {"{{ event.subject }}"}, preview text {"{{ event.preheader }}"}, Smart Sending off → Live.</li>
        <li>Ask Klaviyo support to mark that email as transactional, so it also reaches customers who unsubscribed from marketing.</li>
      </ol>
      {on && !ready ? <Text size="xsmall" className="text-ui-fg-error">Until the flow is live in Klaviyo, store emails are handed to Klaviyo but not delivered.</Text> : null}
    </div>
  )
}

export function KlaviyoCard() {
  const { data, error } = useKlaviyo()
  const [connecting, setConnecting] = useState(false)
  const [confirm, setConfirm] = useState<null | "off" | "disconnect">(null)
  const options = useKlaviyoOptions()
  const test = useTestKlaviyo()
  const sync = useSyncKlaviyo()
  const disconnect = useDisconnectKlaviyo()
  const s = data?.klaviyo
  const lists = useKlaviyoLists(!!s?.connected && !!s?.readable && !!s?.can_manage)
  if (error) return <Section title="Klaviyo"><Text size="small" className="p-4 text-ui-fg-error">{errorMessage(error)}</Text></Section>
  if (!s) return null
  const manage = s.can_manage
  const set = (body: { enabled?: boolean; onsite?: boolean; list_id?: string | null; send_emails?: boolean }, ok: string) =>
    options.mutate(body, { onSuccess: () => toast.success(ok), onError: (e) => toast.error(errorMessage(e)) })
  const runSync = (retry_failed = false) =>
    sync.mutate(
      { retry_failed },
      {
        onSuccess: (r: any) => {
          const errs = Object.entries(r.sync).filter(([k]) => k.endsWith("_error")).map(([, v]) => v)
          if (errs.length) toast.error(String(errs[0]))
          else toast.success(`Synced with Klaviyo: ${Number(r.sync.outbox_sent ?? 0)} sent${r.sync.outbox_failed ? `, ${r.sync.outbox_failed} failed` : ""}${Number(r.sync.outbox_sent ?? 0) === 0 && !r.sync.outbox_failed ? " (nothing was waiting)" : ""}`)
        },
        onError: (e) => toast.error(errorMessage(e)),
      },
    )

  return (
    <Section
      title="Klaviyo"
      actions={
        s.connected ? (
          <Badge tone={!s.readable ? "critical" : s.enabled ? "success" : "neutral"}>{!s.readable ? "Reconnect needed" : s.enabled ? "On" : "Off"}</Badge>
        ) : (
          <Badge tone="neutral">Not connected</Badge>
        )
      }
    >
      <div className="flex flex-col gap-3 p-4">
        <Text size="small" className="text-ui-fg-subtle">
          Email marketing with Klaviyo. Orders, shipments, refunds and checkouts are sent as Klaviyo events (for flows like abandoned checkout and post-purchase), customers and their email marketing consent stay in step both ways, and Klaviyo's on-site script tracks product views, adds to cart and its sign-up forms.
        </Text>
        {s.connected && !s.readable && <Banner tone="critical" title="The saved Klaviyo key can't be read">The server secret changed. Connect again with the private API key.</Banner>}
        {s.connected && s.readable && !s.list && (
          <Banner tone="warning" title="Choose the list newsletter subscribers join">Until a list is chosen, subscribers aren't added in Klaviyo (orders and profiles still go).</Banner>
        )}
        {!!s.counts?.failed && (
          <Banner tone="warning" title={`${s.counts.failed} update${s.counts.failed === 1 ? "" : "s"} couldn't be sent to Klaviyo`}>
            <div className="flex flex-col gap-1">
              {(s.failures ?? []).slice(0, 3).map((f, i) => (
                <span key={i}>{f.label ?? "Update"}: {f.last_error ?? "failed"}</span>
              ))}
            </div>
          </Banner>
        )}
      </div>
      {s.connected && (
        <DefinitionList
          rows={[
            ["Account", s.account_name ? `${s.account_name} (${s.public_key})` : s.public_key ?? "—"],
            ["Private API key", s.key_hint ?? "—"],
            ["Newsletter list", s.list?.name ?? "Not chosen"],
            ["Sent in the last 24 hours", String(s.counts?.sent_24h ?? 0)],
            ["Waiting to send", String(s.counts?.pending ?? 0)],
            ["Last sync", s.last_sync_at ? formatDate(s.last_sync_at) : "Not yet (runs every 5 minutes)"],
            ["Connected", s.connected_at ? formatDate(s.connected_at) : "—"],
            ...(s.last_test ? ([["Last connection test", `${s.last_test.ok ? "OK" : "Failed"} · ${formatDate(s.last_test.at)}`]] as [string, string][]) : []),
          ]}
        />
      )}
      {manage && s.connected && s.readable && (
        <div className="flex flex-col gap-4 border-t border-ui-border-base px-4 py-3">
          <Field label="Newsletter subscribers join" hint="Footer sign-ups, checkout opt-ins and account preferences are added to this Klaviyo list.">
            <Select value={s.list?.id ?? NONE} onValueChange={(v) => set({ list_id: v === NONE ? null : v }, "Newsletter list saved")} disabled={options.isPending || lists.isLoading}>
              <Select.Trigger>
                <Select.Value placeholder={lists.isLoading ? "Loading lists…" : "Choose a list"} />
              </Select.Trigger>
              <Select.Content>
                <Select.Item value={NONE}>No list</Select.Item>
                {(lists.data?.lists ?? []).map((l) => (
                  <Select.Item key={l.id} value={l.id}>{l.name}</Select.Item>
                ))}
              </Select.Content>
            </Select>
          </Field>
          {lists.error && <Text size="xsmall" className="text-ui-fg-error">{errorMessage(lists.error)}</Text>}
          <StoreEmails on={!!s.send_emails} busy={options.isPending} onChange={(v) => set({ send_emails: v }, v ? "Store emails now go through Klaviyo" : "Store emails now go through the store's own email service")} />
          <label className="flex items-start gap-2 text-[13px]">
            <Switch checked={s.onsite ?? true} disabled={options.isPending || !s.enabled} onCheckedChange={(v) => set({ onsite: v }, v ? "On-site tracking on" : "On-site tracking off")} />
            <span>
              On-site tracking and sign-up forms (klaviyo.js)
              <Text size="xsmall" className="text-ui-fg-subtle">Loads on every storefront page except checkout. If the cookie banner is on, only after the visitor accepts.</Text>
            </span>
          </label>
        </div>
      )}
      {manage ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ui-border-base px-4 py-3">
          {s.connected ? (
            <>
              <label className="flex items-center gap-2 text-[13px]">
                <Switch checked={s.enabled} disabled={options.isPending || !s.readable} onCheckedChange={(v) => (v ? set({ enabled: true }, "Klaviyo switched on") : setConfirm("off"))} />
                Send data to Klaviyo
              </label>
              <div className="flex flex-wrap gap-2">
                <Button size="small" variant="secondary" isLoading={test.isPending} onClick={() => test.mutate(undefined, { onSuccess: (r: any) => (r.test.ok ? toast.success(r.test.message) : toast.error(r.test.message)), onError: (e) => toast.error(errorMessage(e)) })}>
                  Test connection
                </Button>
                <Button size="small" variant="secondary" isLoading={sync.isPending} disabled={!s.enabled} onClick={() => runSync(!!s.counts?.failed)}>
                  {s.counts?.failed ? "Retry failed and sync" : "Sync now"}
                </Button>
                <Button size="small" variant="secondary" onClick={() => setConnecting(true)}>Change key</Button>
                <Button size="small" variant="danger" isLoading={disconnect.isPending} onClick={() => setConfirm("disconnect")}>Disconnect</Button>
              </div>
            </>
          ) : (
            <Button size="small" onClick={() => setConnecting(true)}>Connect Klaviyo</Button>
          )}
        </div>
      ) : (
        <Text size="xsmall" className="px-4 pb-4 text-ui-fg-subtle">Only the store owner and staff with full permissions can connect or change Klaviyo.</Text>
      )}
      {connecting && <ConnectDrawer onClose={() => setConnecting(false)} />}
      <Prompt open={confirm !== null} onOpenChange={(o) => !o && setConfirm(null)}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>{confirm === "off" ? "Switch Klaviyo off?" : "Disconnect Klaviyo?"}</Prompt.Title>
            <Prompt.Description>
              {confirm === "off"
                ? "Orders, checkouts and customer changes won't be sent, and klaviyo.js stops loading on the storefront, until you switch it back on. Changes made meanwhile are sent when it's back on."
                : "The key is deleted and anything still waiting to be sent is dropped. Klaviyo keeps everything it already has. You can connect again any time."}
            </Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Cancel</Prompt.Cancel>
            <Prompt.Action
              onClick={() =>
                confirm === "off"
                  ? set({ enabled: false }, "Klaviyo switched off")
                  : disconnect.mutate(undefined, { onSuccess: () => toast.success("Klaviyo disconnected"), onError: (e) => toast.error(errorMessage(e)) })
              }
            >
              {confirm === "off" ? "Switch off" : "Disconnect"}
            </Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </Section>
  )
}

function ConnectDrawer({ onClose }: { onClose: () => void }) {
  const connect = useConnectKlaviyo()
  const [key, setKey] = useState("")
  // Current keys look like pk_<site ID>_<hex>; older ones pk_<hex>.
  const k = key.trim()
  const valid = k.length >= 20 && k.length <= 120 && /^pk_[A-Za-z0-9]+(_[A-Za-z0-9]+)*$/.test(k)
  const submit = () =>
    connect.mutate(
      { api_key: key.trim() },
      {
        onSuccess: (r: any) => {
          toast.success(`Klaviyo connected${r.klaviyo?.account_name ? ` (${r.klaviyo.account_name})` : ""}`)
          if (r.list_error) toast.warning(`Choose the newsletter list on the card: the lists couldn't be loaded (${r.list_error}).`)
          onClose()
        },
        onError: (e) => toast.error(errorMessage(e)),
      },
    )
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>Connect Klaviyo</Drawer.Title>
        </Drawer.Header>
        <Drawer.Body className="flex flex-col gap-4 overflow-y-auto">
          <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-[13px] text-ui-fg-subtle">
            <li>In Klaviyo, click your account name (bottom left) and open <b>Settings → API keys</b>.</li>
            <li>Click <b>Create Private API Key</b> and name it <b>Keel</b>.</li>
            <li>
              Choose <b>Custom Key</b> and give it:
              <ul className="mt-1 flex flex-col gap-0.5 pl-3">
                {SCOPES.map(([scope, access]) => (
                  <li key={scope}>{scope}: <b>{access} Access</b></li>
                ))}
              </ul>
              (A Full Access key works too.)
            </li>
            <li>Create the key, copy it (it starts with <code>pk_</code>) and paste it below.</li>
          </ol>
          <Field label="Private API key" hint={key.trim() && !valid ? "A Klaviyo private key starts with pk_." : undefined}>
            <Input type="password" value={key} autoComplete="off" spellCheck={false} placeholder="pk_…" onChange={(e) => setKey(e.target.value)} />
          </Field>
          <Text size="xsmall" className="text-ui-fg-subtle">
            The key is stored encrypted and never shown again. Keel checks it with Klaviyo before saving, finds your account's public key for the storefront script, and picks a list called "Newsletter" if there is one. Existing customers and subscribers are sent over in the background (subscribers who joined before today don't trigger welcome emails).
          </Text>
        </Drawer.Body>
        <Drawer.Footer>
          <Drawer.Close asChild>
            <Button size="small" variant="secondary">Cancel</Button>
          </Drawer.Close>
          <Button size="small" isLoading={connect.isPending} disabled={!valid} onClick={submit}>
            Connect
          </Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  )
}
